import { useState } from 'react';
import { Pin, PinOff, Pencil } from 'lucide-react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import {
  useCustomerPinnedLogs,
  useUnpinLog,
  useUpdateNoteLog,
  type PinnedLogRow,
} from '@/api/customer-basic';
import { formatDate } from '@/lib/format';
import { TabError, TabLoading } from './tab-states';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

/* ── Edit note dialog ─────────────────────────────────────────────────── */

interface EditNoteDialogProps {
  customerId: number;
  log: PinnedLogRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EditNoteDialog({ customerId, log, open, onOpenChange }: EditNoteDialogProps) {
  const [text, setText] = useState(log.customer_log_description ?? '');
  const update = useUpdateNoteLog(customerId);

  const reset = () => { setText(log.customer_log_description ?? ''); update.reset(); };

  const submit = () => {
    update.mutate(
      { logId: log.customer_log_id, note_description: text.trim() },
      {
        onSuccess: () => {
          onOpenChange(false);
          reset();
        },
      },
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(o) => {
        if (!update.isPending) { onOpenChange(o); if (!o) reset(); }
      }}
      title="Edit Note"
      confirmLabel="Save"
      loading={update.isPending}
      onConfirm={submit}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {update.isError ? (
          <Alert tone="danger" title="Could not save">{errorMessage(update.error)}</Alert>
        ) : null}
        <Field label="Note content" required>
          {(p) => (
            <Input
              {...p}
              placeholder="Note text…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </Field>
      </div>
    </ConfirmDialog>
  );
}

/* ── Single pinned note row ────────────────────────────────────────────── */

interface PinnedNoteRowProps {
  customerId: number;
  log: PinnedLogRow;
}

function PinnedNoteItem({ customerId, log }: PinnedNoteRowProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [unpinOpen, setUnpinOpen] = useState(false);
  const unpin = useUnpinLog(customerId);

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-3)',
        borderBottom: '1px solid var(--vn-border-subtle)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--vn-space-2)' }}>
        <p style={{ margin: 0, lineHeight: 1.5, wordBreak: 'break-word', flex: 1 }}>
          {log.customer_log_description || '—'}
        </p>
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexShrink: 0 }}>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<Pencil size={14} />}
            onClick={() => setEditOpen(true)}
            aria-label="Edit note"
          />
          <Button
            variant="ghost"
            size="sm"
            startIcon={<PinOff size={14} />}
            onClick={() => setUnpinOpen(true)}
            aria-label="Unpin note"
          />
        </div>
      </div>
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(log.created_at)}
      </span>

      {editOpen ? (
        <EditNoteDialog
          customerId={customerId}
          log={log}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      ) : null}

      <ConfirmDialog
        open={unpinOpen}
        onOpenChange={(o) => { if (!unpin.isPending) setUnpinOpen(o); }}
        title="Unpin note"
        description="Remove this note from the pinned list? The note itself is not deleted."
        confirmLabel="Unpin"
        loading={unpin.isPending}
        onConfirm={() => unpin.mutate(log.customer_log_id, { onSuccess: () => setUnpinOpen(false) })}
      >
        {unpin.isError ? (
          <Alert tone="danger" title="Could not unpin">{errorMessage(unpin.error)}</Alert>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

/* ── Main section ──────────────────────────────────────────────────────── */

/**
 * Pinned Notes section for the Basic Information tab — renders the customer's
 * pinned log entries (GET /customers/:id/logs/pinned) with Unpin and Edit actions.
 * Placed above the activity timeline, matching legacy BasicInfo layout.
 */
export function PinnedNotesSection({ customerId }: { customerId: number }) {
  const { data, isLoading, isError, refetch } = useCustomerPinnedLogs(customerId);
  const logs = data?.results ?? [];

  if (isError) return <TabError title="Failed to load pinned notes" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={2} />;
  if (logs.length === 0) {
    return (
      <Card flat>
        <CardHeader><CardTitle>Pinned Notes</CardTitle></CardHeader>
        <CardBody>
          <EmptyState
            icon={<Pin size={24} />}
            title="No pinned notes"
            description="Pin a note from the activity timeline to surface it here."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card flat>
      <CardHeader><CardTitle>Pinned Notes</CardTitle></CardHeader>
      <CardBody style={{ padding: 0 }}>
        {logs.map((log) => (
          <PinnedNoteItem key={log.customer_log_id} customerId={customerId} log={log} />
        ))}
      </CardBody>
    </Card>
  );
}
